'use client';

import React from 'react';

import {
    Box,
    Button,
    Drawer,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    Flex,
    Heading,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    useDisclosure,
    useBreakpointValue,
} from '@chakra-ui/react';
import { BiFilter } from 'react-icons/bi';
import { HiOutlineFilter, HiSearch } from 'react-icons/hi';

import { useAppDispatch, useAppSelector } from '@hooks/store';
import {
    MAXIMUM_MENTOR_EXPERIENCE,
    MINIMUM_MENTOR_EXPERIENCE,
} from '@modules/find-mentor/constants';
import { setFindMentorFilters } from '@modules/find-mentor/slice';
import { sortByFilterArr } from '@utils/helpers';

import Filters from './Filters';

const SearchBar = () => {
    const dispatch = useAppDispatch();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const {
        query,
        orderBy,
        expertise,
        fromDate,
        toDate,
        totalExperienceMax,
        totalExperienceMin,
        totalRating,
    } = useAppSelector(state => state.reducer.mentorFilters);
    const isMobile = useBreakpointValue({ base: true, md: false });

    const handleOrderBy = (value: string) => {
        dispatch(
            setFindMentorFilters({
                orderBy: value,
            })
        );
    };
    const handleSearch = (value: string) => {
        dispatch(
            setFindMentorFilters({
                query: value,
            })
        );
    };

    const getActiveFiltersCount = () => {
        let count = 0;

        if (expertise?.length) count += 1;
        if (fromDate || toDate) count += 1;

        if (
            totalExperienceMin !== MINIMUM_MENTOR_EXPERIENCE ||
            totalExperienceMax !== MAXIMUM_MENTOR_EXPERIENCE
        )
            count += 1;
        if (totalRating) count += 1;

        return count;
    };

    return (
        <>
            <Flex
                flexDir={{ base: 'column', sm: 'column', md: 'row' }}
                gap={{ base: 3, sm: 3, md: 0 }}
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <InputGroup width={{ base: '100%', sm: '100%', md: '35%' }}>
                    <InputLeftElement pointerEvents="none">
                        <HiSearch color="gray" />
                    </InputLeftElement>
                    <Input
                        value={query}
                        onChange={e => handleSearch(e.target.value)}
                        type="text"
                        placeholder="Search by company, name or role"
                    />
                </InputGroup>
                <Box
                    width={{ base: '100%', sm: '100%', md: 'fit-content' }}
                    justifyContent="space-between"
                    display="flex"
                    alignItems="center"
                    gap={2}
                >
                    <Button
                        variant="outline"
                        sx={{ fontWeight: '400' }}
                        display={{ base: 'none', sm: 'none', md: 'none', lg: 'flex' }}
                        leftIcon={<BiFilter />}
                        onClick={onOpen}
                    >
                        Apply Filters{' '}
                        {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
                    </Button>
                    <Select
                        placeholder="Sort By"
                        onChange={e => handleOrderBy(e.target.value)}
                        value={orderBy}
                        size="md"
                        flex={1}
                        width="150px"
                    >
                        {sortByFilterArr.map(item => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </Select>
                    <Box
                        py={1.5}
                        alignItems="center"
                        justifyContent="center"
                        display={{ base: 'flex', sm: 'flex', md: 'flex', lg: 'none' }}
                        width={16}
                        borderWidth="1px"
                        borderColor="border"
                        borderRadius={6}
                    >
                        <HiOutlineFilter onClick={onOpen} fontSize={25} color="grey" />
                    </Box>
                </Box>
            </Flex>
            <Drawer isOpen={isOpen} onClose={onClose} size="sm">
                <DrawerContent px={5}>
                    <DrawerCloseButton />
                    <DrawerHeader pl={0} pb={0} pt={5}>
                        <Heading size="md" fontWeight={500} color="primary.dark">
                            Filters
                        </Heading>
                    </DrawerHeader>
                    <Box display="flex" overflow="auto">
                        <Filters isMobile={isMobile} />
                    </Box>
                </DrawerContent>
            </Drawer>
        </>
    );
};

export default SearchBar;
